import { LineChart, Line, ResponsiveContainer, AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, RadialBarChart, RadialBar } from "recharts";
import { ArrowUpRight, ArrowDownRight, Activity, Layers, Gauge, TrendingUp } from "lucide-react";
import { Link } from "react-router-dom";

const cccSeries = [
  { m: "Jan", ccc: 118 }, { m: "Feb", ccc: 112 }, { m: "Mar", ccc: 105 }, { m: "Apr", ccc: 96 },
  { m: "May", ccc: 88 }, { m: "Jun", ccc: 81 }, { m: "Jul", ccc: 74 }, { m: "Aug", ccc: 69 },
];
const otifSeries = [
  { m: "Jan", otif: 62 }, { m: "Feb", otif: 66 }, { m: "Mar", otif: 71 }, { m: "Apr", otif: 76 },
  { m: "May", otif: 79 }, { m: "Jun", otif: 82 }, { m: "Jul", otif: 84 }, { m: "Aug", otif: 87 },
];
const oeeByAsset = [
  { asset: "CCM-1", oee: 82 }, { asset: "Rolling", oee: 68 }, { asset: "Finishing", oee: 58 },
  { asset: "Packing", oee: 74 }, { asset: "QC", oee: 88 },
];
const marginByLine = [
  { line: "Bearings", cm: 34 }, { line: "Gearboxes", cm: 22 }, { line: "Service AMC", cm: 41 },
  { line: "Refurb", cm: 27 }, { line: "OEM Retro", cm: 18 },
];
const constraintRadial = [{ name: "Constraint OEE", value: 58, fill: "#712ae2" }];

export default function DashboardPage() {
  return (
    <div data-testid="dashboard-page" className="bg-slate-50 min-h-screen">
      {/* Header */}
      <div className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-10 py-8 flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <span className="eyebrow">Executive View · Sample Engagement</span>
            <h1 className="mt-3 text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900">Operational Dashboard</h1>
            <p className="mt-2 text-sm text-slate-500">A representative view of what a live Indusynq engagement delivers on a weekly cadence.</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs">
              <div className="text-slate-500">Client</div>
              <div className="font-semibold text-slate-900">Metals Aftermarket · Plant A+B</div>
            </div>
            <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs">
              <div className="text-slate-500">Period</div>
              <div className="font-semibold text-slate-900 font-mono-tabular">Jan – Aug FY26</div>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-[1440px] px-6 lg:px-10 py-10 space-y-6">
        {/* KPI ROW */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard label="Cash Conversion Cycle" value="69" suffix="days" delta="-49" positive Icon={Activity} spark={cccSeries.map((s) => s.ccc)} />
          <KpiCard label="Working Capital Released" value="₹18.4" suffix="Cr" delta="+₹6.2 Cr QoQ" positive Icon={TrendingUp} spark={[6, 8, 10, 12, 14, 16, 18, 18.4]} />
          <KpiCard label="OTIF (rolling 90d)" value="87" suffix="%" delta="+25 pts YTD" positive Icon={Layers} spark={otifSeries.map((s) => s.otif)} />
          <KpiCard label="Constraint OEE" value="58" suffix="%" delta="+11 pts" positive Icon={Gauge} spark={[45, 48, 50, 52, 54, 55, 57, 58]} />
        </div>

        {/* Row 2 */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card title="CCC Compression Trajectory" subtitle="Target ≤ 60 days by Q4" span={2}>
            <div className="h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={cccSeries} margin={{ left: -12, right: 8, top: 8, bottom: 0 }}>
                  <defs>
                    <linearGradient id="gCCC" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#712ae2" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="#712ae2" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="#E2E8F0" vertical={false} />
                  <XAxis dataKey="m" tick={{ fill: "#64748B", fontSize: 12 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: "#64748B", fontSize: 12 }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12 }} />
                  <Area type="monotone" dataKey="ccc" stroke="#712ae2" strokeWidth={2} fill="url(#gCCC)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </Card>
          <Card title="Constraint Health" subtitle="Bottleneck: Finishing line">
            <div className="h-[220px] relative">
              <ResponsiveContainer width="100%" height="100%">
                <RadialBarChart innerRadius="65%" outerRadius="100%" data={constraintRadial} startAngle={90} endAngle={-270}>
                  <RadialBar background dataKey="value" cornerRadius={10} />
                </RadialBarChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <div className="text-4xl font-semibold text-slate-900">58%</div>
                <div className="text-xs text-slate-500 mt-1">Target 75%</div>
              </div>
            </div>
            <div className="pt-4 border-t border-slate-100 grid grid-cols-3 gap-3 text-center">
              <MicroStat k="Avail." v="76%" />
              <MicroStat k="Perf." v="82%" />
              <MicroStat k="Quality" v="93%" />
            </div>
          </Card>
        </div>

        {/* Row 3 */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card title="OTIF Recovery" subtitle="90-day rolling">
            <div className="h-[220px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={otifSeries} margin={{ left: -12, right: 8, top: 8, bottom: 0 }}>
                  <CartesianGrid stroke="#E2E8F0" vertical={false} />
                  <XAxis dataKey="m" tick={{ fill: "#64748B", fontSize: 12 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: "#64748B", fontSize: 12 }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12 }} />
                  <Line type="monotone" dataKey="otif" stroke="#10B981" strokeWidth={2.5} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Card>

          <Card title="OEE by Asset" subtitle="Weekly average">
            <div className="h-[220px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={oeeByAsset} margin={{ left: -12, right: 8, top: 8, bottom: 0 }}>
                  <CartesianGrid stroke="#E2E8F0" vertical={false} />
                  <XAxis dataKey="asset" tick={{ fill: "#64748B", fontSize: 11 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: "#64748B", fontSize: 12 }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12 }} />
                  <Bar dataKey="oee" fill="#0F172A" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>

          <Card title="Contribution Margin by Line" subtitle="% CM">
            <div className="h-[220px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart layout="vertical" data={marginByLine} margin={{ left: 10, right: 16, top: 4, bottom: 0 }}>
                  <CartesianGrid stroke="#E2E8F0" horizontal={false} />
                  <XAxis type="number" tick={{ fill: "#64748B", fontSize: 12 }} axisLine={false} tickLine={false} />
                  <YAxis type="category" dataKey="line" tick={{ fill: "#64748B", fontSize: 11 }} axisLine={false} tickLine={false} width={72} />
                  <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12 }} />
                  <Bar dataKey="cm" fill="#712ae2" radius={[0, 6, 6, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </div>

        {/* Bottleneck & recommendations */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card title="TOC Bottleneck Map" subtitle="Machine · Process · Skill · Policy · Supplier">
            <ul className="space-y-3 text-sm">
              {[
                { k: "Finishing line", v: "Process", dot: "bg-red-500", pct: 92, note: "Primary system constraint. All flow paced here." },
                { k: "Supplier X (Cast)", v: "Supplier", dot: "bg-amber-500", pct: 46, note: "Single-source, 8-wk lead-time variance." },
                { k: "QC release policy", v: "Policy", dot: "bg-violet-500", pct: 28, note: "Visual release rule ties up 5 days of WIP." },
                { k: "Rolling mill", v: "Machine", dot: "bg-slate-300", pct: 12, note: "Non-constraint. Do not invest here yet." },
              ].map((row, i) => (
                <li key={i} data-testid={`bottleneck-row-${i}`} className="border border-slate-100 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className={`h-2 w-2 rounded-full ${row.dot}`} />
                      <span className="font-semibold text-slate-900">{row.k}</span>
                      <span className="text-[11px] tracking-[0.18em] uppercase text-slate-500 ml-2">{row.v}</span>
                    </div>
                    <span className="font-mono-tabular text-sm text-slate-700">{row.pct}% impact</span>
                  </div>
                  <div className="mt-3 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                    <div className={`h-full ${row.dot}`} style={{ width: `${row.pct}%` }} />
                  </div>
                  <div className="mt-2 text-xs text-slate-500">{row.note}</div>
                </li>
              ))}
            </ul>
          </Card>

          <Card title="Weekly Action Board" subtitle="Owner-assigned quick wins">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] tracking-[0.18em] uppercase text-slate-500 border-b border-slate-100">
                  <th className="py-2 font-semibold">Action</th>
                  <th className="py-2 font-semibold">Owner</th>
                  <th className="py-2 font-semibold">Impact</th>
                  <th className="py-2 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {[
                  { a: "SMED on Finishing changeover", o: "Plant Mgr", i: "+3 pt OEE", s: "In flight" },
                  { a: "Dual-source Supplier X (30%)", o: "SCM Head", i: "₹1.8 Cr risk hedge", s: "Q-4" },
                  { a: "Kill visual-release rule", o: "QA Head", i: "5-day WIP release", s: "Approved" },
                  { a: "Tier-2 collection sprint", o: "CFO", i: "₹4.2 Cr in 60d", s: "In flight" },
                ].map((r, i) => (
                  <tr key={i} data-testid={`action-row-${i}`} className="text-slate-800">
                    <td className="py-3 pr-3">{r.a}</td>
                    <td className="py-3 pr-3 text-slate-600">{r.o}</td>
                    <td className="py-3 pr-3 font-medium">{r.i}</td>
                    <td className="py-3"><span className="inline-block text-[11px] font-semibold px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">{r.s}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </div>

        {/* CTA */}
        <div className="rounded-2xl bg-white border border-slate-200 p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div>
            <div className="text-[11px] tracking-[0.22em] uppercase text-slate-500">Illustrative view</div>
            <h3 className="mt-2 text-xl md:text-2xl font-semibold text-slate-900">Want this dashboard for your plant?</h3>
          </div>
          <div className="flex gap-3">
            <Link to="/diagnostic" data-testid="dashboard-diag-cta" className="inline-flex items-center gap-2 rounded-full border border-slate-300 text-sm font-medium px-5 py-3 hover:bg-slate-50">Run Diagnostic</Link>
            <Link to="/contact" data-testid="dashboard-contact-cta" className="inline-flex items-center gap-2 rounded-full bg-slate-900 text-white text-sm font-medium px-5 py-3">Book working session <ArrowUpRight className="h-4 w-4" /></Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function KpiCard({ label, value, suffix, delta, positive, Icon, spark }) {
  const data = spark.map((v, i) => ({ i, v }));
  return (
    <div className="ind-card p-5" data-testid={`kpi-${label.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="flex items-center justify-between">
        <span className="text-[11px] tracking-[0.22em] uppercase text-slate-500">{label}</span>
        <Icon className="h-4 w-4 text-slate-400" />
      </div>
      <div className="mt-3 flex items-baseline gap-2">
        <span className="number-hero text-3xl text-slate-900">{value}</span>
        <span className="text-sm text-slate-500">{suffix}</span>
      </div>
      <div className={`mt-1 flex items-center gap-1 text-xs font-semibold ${positive ? "text-emerald-600" : "text-red-600"}`}>
        {positive ? <ArrowUpRight className="h-3.5 w-3.5" /> : <ArrowDownRight className="h-3.5 w-3.5" />}
        {delta}
      </div>
      <div className="mt-3 h-10 -mx-1">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data}>
            <Line type="monotone" dataKey="v" stroke={positive ? "#10B981" : "#EF4444"} strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function Card({ title, subtitle, span, children }) {
  return (
    <div className={`ind-card p-5 ${span === 2 ? "lg:col-span-2" : ""}`}>
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="text-[11px] tracking-[0.22em] uppercase text-slate-500">{title}</div>
          <div className="text-sm text-slate-500 mt-0.5">{subtitle}</div>
        </div>
      </div>
      {children}
    </div>
  );
}

function MicroStat({ k, v }) {
  return (
    <div className="rounded-md bg-slate-50 border border-slate-100 py-2">
      <div className="text-[10px] tracking-[0.2em] uppercase text-slate-500">{k}</div>
      <div className="font-mono-tabular text-sm font-semibold text-slate-900">{v}</div>
    </div>
  );
}
